import { useCallback, useEffect, useRef, useState } from 'react';

import { QueryHookOptions, useApolloClient } from '@apollo/client';
import { useStoreContext } from 'features/store';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import {
  ILoyaltyUserOfferItemsQuery,
  LoyaltyOfferRedemptionType,
  LoyaltyPaymentMethod,
  LoyaltyPromotionType,
  LoyaltyServiceMode,
  LoyaltyUserOfferItemsDocument,
  useLoyaltyOfferItemsLazyQuery,
  useLoyaltyUserOfferItemsQuery,
} from 'generated/graphql-gateway';
import {
  useFeatureSortedLoyaltyOffersLazyQuery,
  useLoyaltySystemwideOffersByIdsQuery,
} from 'generated/sanity-graphql';
import { useLocaleSmartBlockContent } from 'hooks/use-locale-smart-block-content/use-locale-smart-block-content';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { parseOffers } from 'state/global-state/models/loyalty/offers/offers.utils';
import { withLazySimpleCache } from 'state/graphql/with-simple-cache';
import { useLocale } from 'state/intl';
import { LANGUAGES } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useServiceModeContext } from 'state/service-mode';
import logger from 'utils/logger';

import {
  AppPersonalizedOffer,
  EngineNonPersonalizedOfferListItem,
  EngineOfferListItem,
  EnginePersonalizedOffer,
  LoyaltyAppliedOffer,
  LoyaltyOffer,
  SanityOfferListItemWithExtras,
  isEngineNonPersonalizedOfferListItem,
  isEnginePersonalizedOffer,
} from '../types';
import { upsertSanityOfferLists } from '../utils';

import { IAppliedRewards, IEntriesIdsMap, IQueryLoyaltyUserOffersOptions } from './types';
import { useLoyaltyIncentivesEvaluation } from './use-loyalty-incentive-evaluation';
import { useLoyaltyUserId } from './use-loyalty-user';
import { usePersonalizedOffers } from './use-personalized-offers';
import { flattenEntriesToMap, parseEntry } from './utils/incentive-evaluation';
import { IPersonalizedData, mergePersonalizedData } from './utils/personalized-offers';

const useCachedFeatureSortedLoyaltyOffersQuery = withLazySimpleCache(
  useFeatureSortedLoyaltyOffersLazyQuery
);

export const useLoyaltyOffers = () => {
  // This flag is parent to the next ones
  const loyaltyOffersEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_OFFERS));
  // This flag replaces Offers 3.0
  const loyaltyStandardOffersEnabled = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_STANDARD_OFFERS)
  );

  const enableLoyaltyStandardOffers = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_STANDARD_OFFERS);
  const offersCooldownEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_GLOBAL_OFFERS_COOLDOWN));
  const client = useApolloClient();
  const [evaluating, setEvaluating] = useState(false);
  const dispatch = useAppDispatch();
  const { loyaltyUserId } = useLoyaltyUserId();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const engineSystemwideOffers = useAppSelector(selectors.loyalty.selectEngineOfferListItems);
  const { serviceMode } = useServiceModeContext();
  const { store } = useStoreContext();
  const sortedOffersRef = useRef<(LoyaltyOffer | AppPersonalizedOffer)[]>([]);
  const cartEntriesIdsMap = useRef<IEntriesIdsMap>({});
  const { language } = useLocale();
  const prevLanguageRef = useRef<LANGUAGES>(language);

  const [
    fetchPersonalizedOfferData,
    { loading: personalizedDataLoading },
  ] = usePersonalizedOffers();
  const storeId = store?.number;

  const { transformSmartBlockContent } = useLocaleSmartBlockContent();
  const [
    queryFeatureSortedLoyaltyOffers,
    { loading: cmsOffersLoading, refetch: refetchCmsOffers },
  ] = useCachedFeatureSortedLoyaltyOffersQuery({
    variables: {
      id: 'feature-loyalty-offers-ui-singleton',
    },
    onCompleted(data) {
      if (data?.LoyaltyOffersUI?.sortedSystemwideOffers) {
        const incomingOffersList = data.LoyaltyOffersUI
          .sortedSystemwideOffers as SanityOfferListItemWithExtras[];

        sortedOffersRef.current = upsertSanityOfferLists(
          sortedOffersRef.current,
          incomingOffersList as LoyaltyOffer[]
        );
      }
    },
  });

  useEffect(() => {
    if (enableLoyaltyStandardOffers) {
      const languageChanged = prevLanguageRef.current !== language;

      queryFeatureSortedLoyaltyOffers({ allowFromCache: !languageChanged });
      prevLanguageRef.current = language;
    }
  }, [language, enableLoyaltyStandardOffers, queryFeatureSortedLoyaltyOffers]);

  const getPersonalizedOffer = useCallback(
    (engineOffer: EnginePersonalizedOffer, personalizedOfferData: IPersonalizedData) => {
      let personalizedOffer: AppPersonalizedOffer | null = null;

      try {
        personalizedOffer = mergePersonalizedData({
          engineOffer,
          transformSmartBlockContent,
          ...personalizedOfferData,
        });
      } catch (error) {
        const message = error?.message ? `Error: ${error.message}` : '';
        logger.error(`Failed processing personalized offer: ${engineOffer.id} - ${message}`);
      }

      return personalizedOffer;
    },
    [transformSmartBlockContent]
  );

  const onUserOffersQueryComplete = useCallback(
    async (data: ILoyaltyUserOfferItemsQuery) => {
      const loyaltyUser = data?.loyaltyUserV2;
      const offerRedemptionAvailableAfter =
        loyaltyUser?.offerRedemptionAvailability?.availableAfter;

      if (offerRedemptionAvailableAfter && offersCooldownEnabled) {
        dispatch(actions.loyalty.setOfferRedemptionAvailableAfter(offerRedemptionAvailableAfter));
      }

      const engineUserOfferItems = (loyaltyUser?.offerItems || []) as EngineOfferListItem[];
      if (!engineUserOfferItems.length) {
        return;
      }

      // Fetch data needed to build personalized offer
      const personalizedOfferData = await fetchPersonalizedOfferData(engineUserOfferItems);

      const appPersonalizedOffers: AppPersonalizedOffer[] = [];
      const engineOfferListItems: EngineNonPersonalizedOfferListItem[] = [];

      engineUserOfferItems.forEach(item => {
        if (isEnginePersonalizedOffer(item) && personalizedOfferData) {
          const personalizedOffer = getPersonalizedOffer(item, personalizedOfferData);
          if (personalizedOffer) {
            appPersonalizedOffers.push(personalizedOffer);
          }
        }

        if (isEngineNonPersonalizedOfferListItem(item)) {
          engineOfferListItems.push(item);
        }
      });

      sortedOffersRef.current = sortedOffersRef.current.concat(appPersonalizedOffers);

      dispatch(actions.loyalty.setCmsOffers(sortedOffersRef.current));
      dispatch(actions.loyalty.setEngineOfferListItems(engineOfferListItems));
      dispatch(actions.loyalty.setAppPersonalizedOffers(appPersonalizedOffers));
    },
    [dispatch, fetchPersonalizedOfferData, getPersonalizedOffer, offersCooldownEnabled]
  );

  const [queryEngineOffers, { loading: engineOffersLoading }] = useLoyaltyOfferItemsLazyQuery({
    fetchPolicy: 'network-only',
    variables: {
      where: {
        serviceMode: LoyaltyServiceMode[serviceMode || ''] || undefined,
        storeId,
        omitInvalids: false,
        includeOfferGroups: true,
      },
    },
    onCompleted: data => {
      if (data?.loyaltyOfferItems?.length) {
        dispatch(actions.loyalty.setCmsOffers(sortedOffersRef.current));
        dispatch(
          actions.loyalty.setGuestEngineOfferListItems(
            data.loyaltyOfferItems as EngineOfferListItem[]
          )
        );
      }
    },
  });

  // Attach applied offers ids to get the full collection of offers from CMS
  const appliedLoyaltyOffersIds = appliedOffers.map(({ cmsId }) => cmsId || '');

  const { loading: queryUserAppliedOffersLoading } = useLoyaltyUserOfferItemsQuery({
    fetchPolicy: 'no-cache',
    skip: !loyaltyUserId || !appliedLoyaltyOffersIds.length || engineSystemwideOffers.length > 0,
    variables: {
      loyaltyId: loyaltyUserId,
      where: {
        ids: appliedOffers.map(({ id }) => id || ''),
        includeOfferGroups: true,
      },
    },
    onCompleted: onUserOffersQueryComplete,
  });

  const { loading: swoffersLoading } = useLoyaltySystemwideOffersByIdsQuery({
    variables: {
      ids: appliedLoyaltyOffersIds,
    },
    skip: !appliedLoyaltyOffersIds.length,
    onCompleted(data) {
      if (data?.allSystemwideOffers) {
        sortedOffersRef.current = sortedOffersRef.current.concat(
          data.allSystemwideOffers as LoyaltyOffer[]
        );

        dispatch(actions.loyalty.setCmsOffers(sortedOffersRef.current));

        const upsizeAndDiscountIds = appliedOffers
          .filter(({ cartId }) => cartId === DISCOUNT_OFFER_CART_ID)
          .map(({ id }) => id || '');

        if (upsizeAndDiscountIds.length) {
          queryUserOffers({
            variables: {
              loyaltyId: loyaltyUserId,
              where: {
                omitInvalids: false,
                ids: upsizeAndDiscountIds,
              },
            },
          });
        }
      }
    },
  });

  const [queryUserOffersLoading, setQueryUserOffersLoading] = useState(false);
  const queryUserOffers = useCallback(
    async ({ variables: { where, ...otherVariables }, ...options }: QueryHookOptions) => {
      setQueryUserOffersLoading(true);
      dispatch(actions.loyalty.setOffersLoading(true));

      try {
        const { data } = await client.query<ILoyaltyUserOfferItemsQuery>({
          ...options,
          fetchPolicy: 'no-cache',
          query: LoyaltyUserOfferItemsDocument,
          variables: {
            ...otherVariables,
            where: {
              ...where,
              includeOfferGroups: true,
            },
          },
        });

        if (data) {
          onUserOffersQueryComplete(data);
        }
      } catch (e) {
        logger.error(`Error fetching user offers: ${e}`);
      } finally {
        setQueryUserOffersLoading(false);
        dispatch(actions.loyalty.setOffersLoading(false));
      }
    },
    [client, dispatch, onUserOffersQueryComplete]
  );

  const queryLoyaltyUserOffers = useCallback(
    ({
      redemptionTypes,
      omitInvalids = false,
    }: {
      redemptionTypes: LoyaltyOfferRedemptionType[];
      omitInvalids?: boolean;
    }) => ({
      loyaltyId,
      cartEntries,
      appliedRewards,
      appliedLoyaltyOffers = [],
    }: IQueryLoyaltyUserOffersOptions) => {
      const parsedEntries = cartEntries?.reduce(
        parseEntry({ type: LoyaltyPromotionType.OFFER, incentivesToExclude: appliedRewards }),
        []
      );

      cartEntriesIdsMap.current = (parsedEntries || []).reduce(flattenEntriesToMap, {});

      queryUserOffers({
        variables: {
          loyaltyId,
          where: {
            appliedIncentives: parseOffers(appliedLoyaltyOffers),
            cartEntries: parsedEntries,
            serviceMode: serviceMode || undefined,
            redemptionTypes,
            storeId,
            omitInvalids,
          },
        },
      });
    },
    [queryUserOffers, serviceMode, storeId]
  );

  // Offers without loyaltyId should always be of redemption type STANDARD
  const queryLoyaltyOffers = useCallback(() => {
    queryEngineOffers({
      variables: {
        where: {
          serviceMode: LoyaltyServiceMode[serviceMode || ''],
          storeId,
          redemptionTypes: [LoyaltyOfferRedemptionType.STANDARD],
          includeOfferGroups: true,
        },
      },
    });
  }, [queryEngineOffers, serviceMode, storeId]);

  const queryLoyaltyUserStandardOffers = useCallback(
    queryLoyaltyUserOffers({ redemptionTypes: [LoyaltyOfferRedemptionType.STANDARD] }),
    [queryLoyaltyUserOffers]
  );

  const { evaluateLoyaltyIncentives } = useLoyaltyIncentivesEvaluation();

  const evaluateLoyaltyUserOffers = useCallback(
    async (
      loyaltyId: string,
      appliedLoyaltyOffers: LoyaltyAppliedOffer[],
      cartEntries?: ILegacyCartEntry[],
      appliedLoyaltyRewards?: IAppliedRewards | null,
      subtotalAmount?: number,
      paymentMethod?: LoyaltyPaymentMethod | null
    ) => {
      const parsedEntries = cartEntries?.reduce(
        parseEntry({
          type: LoyaltyPromotionType.OFFER,
          incentivesToExclude: appliedLoyaltyRewards,
        }),
        []
      );

      cartEntriesIdsMap.current = (parsedEntries || []).reduce(flattenEntriesToMap, {});

      setEvaluating(true);

      try {
        const evaluationResult = await evaluateLoyaltyIncentives({
          loyaltyId,
          appliedIncentives: appliedLoyaltyOffers,
          incentiveType: LoyaltyPromotionType.OFFER,
          cartEntries: parsedEntries,
          subtotalAmount,
          paymentMethod,
          serviceMode,
          storeId,
        });

        if (evaluationResult) {
          dispatch(actions.loyalty.setOffersFeedbackMap(evaluationResult));
        }
      } finally {
        setEvaluating(false);
      }
    },
    [dispatch, evaluateLoyaltyIncentives, serviceMode, storeId]
  );

  useEffect(() => {
    dispatch(
      actions.loyalty.setOffersLoading(
        queryUserOffersLoading ||
          engineOffersLoading ||
          personalizedDataLoading ||
          cmsOffersLoading ||
          swoffersLoading ||
          evaluating ||
          queryUserAppliedOffersLoading
      )
    );
  }, [
    dispatch,
    queryUserOffersLoading,
    engineOffersLoading,
    personalizedDataLoading,
    cmsOffersLoading,
    swoffersLoading,
    evaluating,
    queryUserAppliedOffersLoading,
  ]);

  return {
    evaluateLoyaltyUserOffers,
    loyaltyOffersEnabled,
    loyaltyStandardOffersEnabled,
    queryLoyaltyOffers,
    queryLoyaltyUserStandardOffers,
    refetchCmsOffers,
    cmsOffersLoading,
  };
};
